import { Component } from 'react'
import styled from '@emotion/styled'
import { Spinner } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { Icons, Box, Button, ItemInfoModal, StatusLabel, StatusChip, PrivateImage } from 'stylewhere/components'
import { GroupedItemRowProps, GroupedItemConfigProps, DecodedItem, TmrProduct } from 'stylewhere/api'
import { closeModal, openModal, isQcStatusProduct } from 'stylewhere/shared/utils'
import { T, __ } from 'stylewhere/i18n'
import { AppStore } from 'stylewhere/shared'

interface State {
  modalVisible: boolean
}

export class GroupedItemRow extends Component<GroupedItemRowProps, State> {
  static Skeleton = styled(Box)``

  getUnknownItemRowCode = () => {
    const { decodedItem } = this.props
    if (decodedItem.identifierCode) return decodedItem.identifierCode
    if (decodedItem.epc) return decodedItem.epc
    return 'Identifier N/A'
  }

  renderUnknownItemRow = () => {
    const { decodedItem, onRemoveItem, unknownHeight, mb } = this.props
    return (
      <Row row mb={mb} height={unknownHeight || 66} bkg="#FF965B" center>
        <Box flex ph={16} pl={0}>
          <ReadIdentifierCode>{this.getUnknownItemRowCode()}</ReadIdentifierCode>
        </Box>
        {!!onRemoveItem && (
          <Box m={8}>
            <Button onClick={() => onRemoveItem(decodedItem)} variant="secondary" size="small" circle>
              <Icons.Delete width={24} height={24} />
            </Button>
          </Box>
        )}
      </Row>
    )
  }

  showItemModal = () => {
    const { decodedItem } = this.props
    if (decodedItem.item && !decodedItem.item.fakeItemID) {
      openModal({
        id: 'item-modal',
        closeOnOverlayClick: true,
        modal: <ItemInfoModal visible itemId={decodedItem.item.id} onClose={() => closeModal('item-modal')} />
      })
    }
  }

  isWarningZone = () => {
    const { operation, itemZoneBase, decodedItem } = this.props
    if (operation?.enabledCheckItemZone && itemZoneBase && decodedItem.item?.zone) {
      return decodedItem.item.zone.id !== itemZoneBase
    }
    return false
  }

  getCode = () => {
    const { decodedItem, configuration } = this.props
    if (decodedItem?.item) {
      const { product } = decodedItem.item || {}
      const productCode = product?.code ? product.code : 'N/A'
      if (configuration?.useProductCode) {
        return productCode
      }
      if (decodedItem.item.attributes?.wam) {
        let wam = decodedItem.item.attributes.wam.value || decodedItem.item.attributes.wam
        if (wam) {
          if (wam.length > 5) {
            wam = wam.substr(0, wam.length - 5) + ' ' + wam.substr(wam.length - 5, 5)
          }
          return wam
        }
        return productCode
      } else {
        return productCode
      }
    }
    return 'N/A'
  }

  renderProductChips = (
    product: TmrProduct | undefined,
    supplier: string | undefined,
    configuration: GroupedItemConfigProps | undefined,
    viewCqStatus: boolean
  ) => {
    const productSize = product?.size?.value || 'N/A'
    const productColor = product?.color?.value || 'N/A'
    const productMaterial = product?.material?.value || 'N/A'
    const productSizeColor =
      product?.attributes?.tagliaColore?.value && product.attributes.tagliaColore.value !== ''
        ? product.attributes.tagliaColore.value
        : 'N/A'
    const justify = viewCqStatus ? 'flex-start' : 'flex-end'
    if (!product) {
      return (
        <ChipsProductBox flex row vcenter justify={justify}>
          <ChipsProduct>
            <ChipsProductLabel>{__(T.custom.prodottoSconosciuto)}</ChipsProductLabel>
          </ChipsProduct>
        </ChipsProductBox>
      )
    }
    return (
      <ChipsProductBox flex row vcenter justify={justify}>
        <ChipsProduct>
          <ChipsProductLabel>{__(T.misc.material) + ':'}</ChipsProductLabel>
          <ChipsProductValue>{productMaterial}</ChipsProductValue>
        </ChipsProduct>
        {productSizeColor ? (
          <ChipsProduct>
            <ChipsProductLabel>{__(T.misc.size) + ' - ' + __(T.misc.color) + ':'}</ChipsProductLabel>
            <ChipsProductValue>{productSizeColor}</ChipsProductValue>
          </ChipsProduct>
        ) : (
          <>
            <ChipsProduct>
              <ChipsProductLabel>{__(T.misc.size) + ':'}</ChipsProductLabel>
              <ChipsProductValue>{productSize}</ChipsProductValue>
            </ChipsProduct>
            <ChipsProduct>
              <ChipsProductLabel>{__(T.misc.color) + ':'}</ChipsProductLabel>
              <ChipsProductValue>{productColor}</ChipsProductValue>
            </ChipsProduct>
          </>
        )}
        {configuration?.showSupplier && supplier && (
          <ChipsProduct>
            <ChipsProductLabel>{__(T.report_tbox.supplier) + ':'}</ChipsProductLabel>
            <ChipsProductValue>{supplier}</ChipsProductValue>
          </ChipsProduct>
        )}
      </ChipsProductBox>
    )
  }

  renderCqStatus = (viewCqStatus: boolean, decodedItem: DecodedItem) => {
    if (!viewCqStatus) return null
    const cqStatus = isQcStatusProduct(decodedItem.item)
    const hasCqStatus = decodedItem.item?.attributes?.qcStatus
    return (
      <Box width={75} center>
        {hasCqStatus && hasCqStatus !== 'LOADING' && (
          <StatusLabel text={'CQ ' + (cqStatus ? 'OK' : 'KO')} status={cqStatus ? 'received' : 'in_inbound'} />
        )}
        {hasCqStatus && hasCqStatus === 'LOADING' && <Spinner color={'#000'} />}
      </Box>
    )
  }

  renderCounter = (decodedItem: DecodedItem) => {
    const { disabledCounter, operation } = this.props
    const hasChecklist = operation && 'hasChecklist' in operation && operation.hasChecklist

    if (!hasChecklist || disabledCounter) return null

    const read = decodedItem.detected ?? 0
    const counterValue = decodedItem.expected ? `${read}/${decodedItem.expected}` : `${read}${hasChecklist ? '/0' : ''}`
    return (
      <Box center ml={15}>
        <SmallCounter
          mr={0}
          ph={20}
          detected={read}
          expected={decodedItem.expected ?? 0}
          unexpected={decodedItem.unexpected ?? 0}
          noChecklist={!hasChecklist}
        >
          {counterValue}
        </SmallCounter>
      </Box>
    )
  }

  renderItemRow = () => {
    const { decodedItem, mb, configuration, hideProductInfo } = this.props
    const { product } = decodedItem.item || {}
    const showDescription = product?.description && product.description !== ''
    const viewCqStatus = !configuration?.hideCqStatus
    const viewImage = configuration?.showProductImage && product?.code
    const supplier = decodedItem?.item?.supplier
    return (
      <Row row mb={mb} onClick={this.showItemModal}>
        {viewImage && AppStore.getShowProductImage() && (
          <Box center mr={15}>
            <PrivateImage product={product} width={100} height={100} />
          </Box>
        )}
        <Box width={viewCqStatus ? '25%' : viewImage ? '30%' : '40%'} mr={15} ml={0}>
          <Box vcenter flex>
            <ReadIdentifierCode>{this.getCode()}</ReadIdentifierCode>
            {showDescription && !hideProductInfo && <DescriptionLabel>{product.description}</DescriptionLabel>}
            {this.isWarningZone() && (
              <StatusChip mt={8} customStatusType="warning" status={__(T.misc.warning_zone_items)} />
            )}
          </Box>
        </Box>
        <Box flex ml={15} mr={viewCqStatus ? 15 : 0} vcenter row>
          {this.renderProductChips(product, supplier, configuration, viewCqStatus)}
        </Box>
        {this.renderCqStatus(viewCqStatus, decodedItem)}
        {this.renderCounter(decodedItem)}
      </Row>
    )
  }

  render() {
    const { decodedItem } = this.props
    if (!decodedItem.item?.id) return this.renderUnknownItemRow()

    return this.renderItemRow()
  }
}

const Row = styled(Box)<{ mb?; bkg?: string }>`
  margin-bottom: ${({ mb }) => (mb ? mb + 'px' : '20px')};
  border: 2px solid #edecec;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background-color: ${({ theme, bkg }) => bkg || theme.background2};
  padding: 10px 15px;
`

const ReadIdentifierCode = styled.p`
  font-weight: bold;
  font-size: 16pt;
  line-height: 18pt;
`

const DescriptionLabel = styled.span`
  font-size: 14px;
  line-height: 18px;
  display: flex;
  align-items: center;
  margin-top: 3px;
`

const ChipsProductBox = styled(Box)`
  flex-wrap: wrap;
`

const ChipsProduct = styled.div`
  padding: 0 6px;
  border-radius: 5px;
  text-align: center;
  background-color: #ededed;
  margin-right: 3px;
  margin-top: 3px;
`

const ChipsProductLabel = styled.span`
  font-size: 16px;
`

const ChipsProductValue = styled.span`
  font-size: 16px;
  font-weight: bold;
  margin-left: 5px;
`

const Quantity = styled(motion.div)<{ status }>`
  border-radius: 5px;
  background-color: ${({ status }) => (status ? '#74f1a9' : '#FBBF66')};
  font-weight: bold;
  font-size: 16px;
  height: 42px;
  padding: 0px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const QuantityValue = styled.span`
  font-size: 30px;
  font-weight: 900;
`

const SmallCounter = styled(Box)<{
  detected?: number
  expected?: number
  unexpected?: number
  noChecklist?: boolean
}>`
  font-size: 50px;
  font-weight: bold;
  text-align: center;
  min-width: 100px;
  width: 100%;
  align-self: center;
  background-color: ${({ detected = 0, expected = 0, unexpected = 0, noChecklist = false }) => {
    if (noChecklist) return '#F0F0F0'
    if (detected + unexpected > expected) return '#F2B57D'
    if (noChecklist) return '#75EBA8'
    if (unexpected > 0) return '#F2B57D'
    if (detected < expected) return '#EDEDED'
    if (detected === expected) return '#75EBA8'
    return '#F0F0F0'
  }};
  border-radius: 10px;
`
